import MobxReactForm from 'mobx-react-form';
import dvr from 'mobx-react-form/lib/validators/DVR';
import validatorjs from 'validatorjs';
import _get from 'lodash/get';
import AuthStore from '../../../../stores/AuthStore';
import ProfileStore from '../../../../stores/ProfileStore';
import NotificationStore from '../../../../stores/NotificationStore';
import { GENDERS, TITLES } from '../../../../framework/constants/profile';
import { i18nextInstance } from '../../../../../i18n';
import { PHONE_PREFIX_DEFAULT, PHONE_PREFIXES } from '../../../../framework/constants/forms';
const interceptor = {
    key: 'default',
    call: ({ change }) => {
        if (change.newValue !== null && change.newValue !== undefined) {
            return change;
        }
        change.newValue = '';
        return change;
    }
};
const createDropDownInterceptor = (values) => ({
    key: 'default',
    call: ({ change }) => {
        const value = values.find(({ value }) => value === change.newValue);
        change.newValue = value !== undefined ? value : '';
        return change;
    }
});
export const fields = [
    {
        name: 'gender',
        label: 'modals:offerRequest.gender',
        placeholder: 'modals:offerRequest.genderPlaceholder',
        rules: 'required',
        extra: GENDERS,
        output: (data) => {
            return data ? data.value : null;
        },
        interceptors: [createDropDownInterceptor(GENDERS)]
    },
    {
        name: 'title',
        label: 'modals:offerRequest.title',
        placeholder: 'modals:offerRequest.titlePlaceholder',
        extra: TITLES,
        output: (data) => {
            return data ? data.value : null;
        },
        interceptors: [createDropDownInterceptor(TITLES)]
    },
    {
        name: 'firstName',
        label: 'modals:offerRequest.firstName',
        placeholder: 'modals:offerRequest.firstNamePlaceholder',
        rules: 'required|string|firstName',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'lastName',
        label: 'modals:offerRequest.lastName',
        placeholder: 'modals:offerRequest.lastNamePlaceholder',
        rules: 'required|string|lastName',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'street',
        label: 'modals:offerRequest.street',
        placeholder: 'modals:offerRequest.streetPlaceholder',
        rules: 'required|string',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'number',
        label: 'modals:offerRequest.number',
        placeholder: 'modals:offerRequest.numberPlaceholder',
        rules: 'required|string',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'zip',
        label: 'modals:offerRequest.zip',
        placeholder: 'modals:offerRequest.zipPlaceholder',
        rules: 'required|string',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'city',
        label: 'modals:offerRequest.city',
        placeholder: 'modals:offerRequest.cityPlaceholder',
        rules: 'required|string',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'phoneMain',
        label: 'modals:offerRequest.phoneMain',
        placeholder: 'modals:offerRequest.phoneMainPlaceholder',
        fields: [
            {
                name: 'countryCode',
                placeholder: 'modals:offerRequest.countryCodePlaceholder',
                extra: PHONE_PREFIXES,
                value: PHONE_PREFIX_DEFAULT,
                rules: 'required',
                output: (data) => {
                    return data.value ? data.value : data;
                }
            },
            {
                name: 'areaCode',
                placeholder: 'modals:offerRequest.areaCodePlaceholder',
                type: 'tel',
                rules: 'required|phone_areaCode'
            },
            {
                name: 'phoneNumber',
                placeholder: 'modals:offerRequest.phoneNumberPlaceholder',
                type: 'tel',
                rules: 'required|phone_phoneNumber'
            }
        ],
        output: (value) => {
            const { countryCode, areaCode, phoneNumber } = value;
            return `${countryCode ? countryCode : ''}${areaCode}${phoneNumber}`;
        },
        interceptors: [{
                key: 'default',
                call: ({ field, change }) => {
                    if (change.newValue) {
                        const countryCode = field.$('countryCode');
                        const areaCode = field.$('areaCode');
                        const phoneNumber = field.$('phoneNumber');
                        countryCode.set('rules', '');
                        countryCode.set('default', '');
                        areaCode.set('rules', '');
                        areaCode.set('default', '');
                        phoneNumber.set('rules', 'required|phone');
                        phoneNumber.set('default', change.newValue);
                    }
                    return null;
                }
            }]
    },
    {
        name: 'email',
        type: 'email',
        label: 'modals:offerRequest.email',
        placeholder: 'modals:offerRequest.emailPlaceholder',
        rules: 'email|required_with:password',
        value: '',
        interceptors: [interceptor]
    },
    {
        name: 'password',
        label: 'modals:offerRequest.password',
        placeholder: 'modals:offerRequest.passwordPlaceholder',
        type: 'password',
        rules: 'password|required_with:email',
        value: '',
        interceptors: [interceptor]
    }
];
const hooks = {
    onSuccess: (form) => {
        const { email, password, ...userAddress } = form.values();
        return ProfileStore.updateUserProfile({ userAddress })
            .then(() => ProfileStore.getProfileData())
            .then(() => {
            if (ProfileStore.tempPassword) {
                return AuthStore.changePassword(ProfileStore.tempPassword, password)
                    .then(() => AuthStore.authenticate(email, password));
            }
        })
            .catch((reason) => {
            const { errors } = reason;
            if (Array.isArray(errors)) {
                errors.forEach((error) => {
                    if (error.status === '500') {
                        NotificationStore.addNotification({
                            message: i18nextInstance.t('modals:offerRequest.saveDataFailed'),
                            level: 'error'
                        });
                        return;
                    }
                    const pointer = _get(error, 'source.pointer', '').split('/');
                    const fieldName = pointer.length ? pointer[pointer.length - 1] : '';
                    const errorDetail = _get(error, 'detail', '');
                    if (form.has(fieldName)) {
                        NotificationStore.addNotification({
                            message: `Error in field "${fieldName}": ${errorDetail}`,
                            level: 'error'
                        });
                        const field = form.$(fieldName);
                        if (field.hasNestedFields) {
                            field.each((field) => {
                                field.invalidate();
                            });
                        }
                        else {
                            field.invalidate(errorDetail);
                        }
                    }
                });
            }
            throw reason;
        });
    }
};
const plugins = {
    dvr: dvr({
        package: validatorjs
    })
};
const options = {
    showErrorsOnReset: false,
    validateOnBlur: false,
    validateOnChange: true
};
export default new MobxReactForm({}, { hooks, plugins, options });
